/**
 * AudioPlayerContext — global "TOVÍAS TE LO CUENTA" mini-player.
 *
 * Owns ONE persistent <audio> element for the whole app. The bar UI is
 * rendered by `<AudioPlayerBar>` (imported once in App.js, outside the
 * Router's <Routes>), so navigating between /rutas → /r/:id → /dashboard
 * doesn't interrupt playback.
 *
 * Public API (via `useAudioPlayer()`):
 *   const { play, close, isPlaying, currentGuide, ... } = useAudioPlayer();
 *   play({ placeId, placeName, placeCity })
 *
 * The context resolves the audio guide from the backend
 * `/api/audio-guides?place_id=…` on `play()` — the trigger button
 * doesn't need to know the guide id.
 */
import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AudioPlayerContext = createContext(null);

export const useAudioPlayer = () => {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx) {
    // Fail-open: if a page renders the trigger button outside the
    // provider (shouldn't happen once App.js mounts it), return a
    // no-op stub instead of crashing.
    return {
      play: () => {},
      close: () => {},
      togglePlay: () => {},
      seek: () => {},
      setMinimized: () => {},
      isPlaying: false,
      isLoading: false,
      currentGuide: null,
      currentPlace: null,
      currentTime: 0,
      duration: 0,
      minimized: false,
      error: null,
    };
  }
  return ctx;
};

export function AudioPlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [currentGuide, setCurrentGuide] = useState(null); // { id, title, language, ... }
  const [currentPlace, setCurrentPlace] = useState(null); // { id, name, city, image_file_id }
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [minimized, setMinimized] = useState(false);
  const [error, setError] = useState(null);

  // Ensure the <audio> element exists once.
  useEffect(() => {
    if (audioRef.current) return;
    const a = new Audio();
    a.preload = 'metadata';
    audioRef.current = a;

    const onTime = () => setCurrentTime(a.currentTime || 0);
    const onMeta = () => setDuration(a.duration || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnd = () => setIsPlaying(false);
    const onError = () => {
      setError('No se pudo reproducir la audioguía');
      setIsLoading(false);
      setIsPlaying(false);
    };
    const onWaiting = () => setIsLoading(true);
    const onCanPlay = () => setIsLoading(false);

    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnd);
    a.addEventListener('error', onError);
    a.addEventListener('waiting', onWaiting);
    a.addEventListener('canplay', onCanPlay);

    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnd);
      a.removeEventListener('error', onError);
      a.removeEventListener('waiting', onWaiting);
      a.removeEventListener('canplay', onCanPlay);
      a.pause();
      audioRef.current = null;
    };
  }, []);

  const play = useCallback(async ({ placeId, placeName, placeCity, language = 'es', directAudioUrl, placeCoverImage }) => {
    if (!placeId) return;
    setError(null);
    setMinimized(false);
    // If we're already playing this guide, just resume.
    if (currentGuide && currentPlace?.id === placeId) {
      audioRef.current?.play().catch(() => {});
      return;
    }
    setIsLoading(true);
    setCurrentTime(0);
    setDuration(0);
    // sep 2026 · lugares importados desde una Ruta Tovías curada llevan
    // su propio `audio_url` (GridFS directo) y no tienen un `place_id`
    // global en el catálogo de `audio_guides` — nos lo pasan ya resuelto
    // y nos saltamos la llamada a `/api/audio-guides`.
    if (directAudioUrl) {
      setCurrentGuide({ id: placeId, title: placeName });
      setCurrentPlace({ id: placeId, name: placeName, city: placeCity, cover_image: placeCoverImage || null });
      if (audioRef.current) {
        audioRef.current.src = directAudioUrl.startsWith('http') ? directAudioUrl : `${BACKEND_URL}${directAudioUrl}`;
        audioRef.current.load();
        try {
          await audioRef.current.play();
        } catch (_e) {
          setError('No se pudo reproducir la audioguía');
          setIsLoading(false);
        }
      }
      return;
    }
    try {
      const { data: guide } = await axios.get(
        `${BACKEND_URL}/api/audio-guides`,
        { params: { place_id: placeId, language } }
      );
      // Also fetch the place for the cover image + human context.
      let place = { id: placeId, name: placeName, city: placeCity };
      try {
        const { data: pl } = await axios.get(`${BACKEND_URL}/api/places/${placeId}`);
        place = { ...place, ...pl };
      } catch (_) { /* non-blocking */ }
      setCurrentGuide(guide);
      setCurrentPlace(place);
      if (audioRef.current) {
        audioRef.current.src = `${BACKEND_URL}/api/audio-guides/${guide.id}/stream`;
        audioRef.current.load();
        await audioRef.current.play();
      }
    } catch (e) {
      const status = e?.response?.status;
      setError(status === 404
        ? 'Aún no tenemos audioguía para este lugar'
        : 'No se pudo cargar la audioguía');
      setIsLoading(false);
    }
  }, [currentGuide, currentPlace]);

  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a || !currentGuide) return;
    if (a.paused) {
      a.play().catch(() => {});
    } else {
      a.pause();
    }
  }, [currentGuide]);

  const seek = useCallback((seconds) => {
    const a = audioRef.current;
    if (!a || !isFinite(seconds)) return;
    a.currentTime = Math.max(0, Math.min(seconds, a.duration || seconds));
  }, []);

  const close = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    setCurrentGuide(null);
    setCurrentPlace(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setError(null);
    setMinimized(false);
  }, []);

  const value = {
    play,
    close,
    togglePlay,
    seek,
    setMinimized,
    isPlaying,
    isLoading,
    currentGuide,
    currentPlace,
    currentTime,
    duration,
    minimized,
    error,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
}
